/**
 * AuthContext
 * -----------
 * Minimal auth state provider powered by the backend's cookie-based JWT auth.
 *
 *   * On mount, hits `GET /api/auth/me` using withCredentials so the
 *     httpOnly `access_token` cookie is automatically sent. Any user the
 *     browser is still logged in as gets rehydrated immediately.
 *   * `user` is:
 *       undefined -> still checking (render a skeleton / loader)
 *       null      -> confirmed logged-out
 *       UserPublic -> authenticated
 *   * `login` / `register` / `logout` hit their respective endpoints and
 *     update local state atomically.
 *
 * API surface is intentionally tiny -- no refresh-token plumbing here
 * because the backend's refresh is cookie-driven and transparent.
 */
import * as React from "react";
import { api, extractApiError } from "../lib/api";
import type { UserPublic } from "../lib/types";

interface LoginInput {
  email: string;
  password: string;
}

interface RegisterInput {
  email: string;
  username: string;
  password: string;
  full_name?: string;
}

interface AuthContextValue {
  /** undefined = checking, null = logged out, object = logged in. */
  user: UserPublic | null | undefined;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Force re-fetch of /auth/me (e.g. after role changes). */
  refresh: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<UserPublic | null | undefined>(undefined);

  const refresh = React.useCallback(async () => {
    try {
      // `validateStatus` tells axios that 401 is an expected outcome
      // (unauthenticated bootstrap), so it never raises nor logs red.
      const res = await api.get<UserPublic>("/auth/me", {
        validateStatus: (s) => s < 500,
      });
      if (res.status >= 200 && res.status < 300) {
        setUser(res.data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const login = React.useCallback(async ({ email, password }: LoginInput) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data?.user as UserPublic);
    } catch (err) {
      throw new Error(extractApiError(err));
    }
  }, []);

  const register = React.useCallback(async (input: RegisterInput) => {
    try {
      const res = await api.post("/auth/register", input);
      setUser(res.data?.user as UserPublic);
    } catch (err) {
      throw new Error(extractApiError(err));
    }
  }, []);

  const logout = React.useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore network errors -- we still want local state to reflect logout.
    }
    setUser(null);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ user, login, register, logout, refresh }),
    [user, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth
 *
 * Hook that lets any component subscribe to auth state. Throws if used
 * outside an AuthProvider so misuse fails loudly instead of silently
 * rendering logged-out UI to an authed user.
 */
export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
